import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import {
  FormDelta,
  MAX_DELTA_PATHS,
  computeFormDelta,
  countDeltaPaths,
  isEmptyDelta,
  normalizeFormData,
} from './form-delta.util';

export type AutosaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error' | 'expired';

export interface AutosaveState {
  status: AutosaveStatus;
  lastSavedAt: Date | null;
  message: string | null;
}

export interface AutosaveConfig {
  submissionId: string;
  // Reads the form's current data, called on every cycle
  getData: () => any;
  // Snapshot to diff against, normally the data after hydration
  baseline: any;
  // Autosave is skipped while this returns false
  canSave?: () => boolean;
  // Asked before each write, false cancels it
  beforeSave?: () => Promise<boolean>;
  // Called after a write with the server's `autosavedAt`
  onSaved?: (autosavedAt: string | null) => void;
  intervalMs?: number;
}

// Writes the fields the user changed, on an interval
// Only a delta is sent, the status is never touched and nothing is validated
// Uses fetch, not HttpClient, because the auth interceptor logs out on any 401
// A background autosave must not throw the user out of the form
@Injectable({ providedIn: 'root' })
export class FormAutosaveService implements OnDestroy {
  private static readonly DEFAULT_INTERVAL_MS = 60000;

  private readonly state$ = new BehaviorSubject<AutosaveState>({
    status: 'idle',
    lastSavedAt: null,
    message: null,
  });
  readonly changes = this.state$.asObservable();

  private submissionId: string | null = null;
  private getData: (() => any) | null = null;
  private canSave: () => boolean = () => true;
  private beforeSave: (() => Promise<boolean>) | null = null;
  private onSaved: ((autosavedAt: string | null) => void) | null = null;
  private baseline: any = {};
  private dirty = false;
  private inFlight = false;
  private stopped = true;
  private timer: any = null;
  private running: Promise<void> | null = null;

  constructor(private authService: AuthService) {}

  get state(): AutosaveState {
    return this.state$.value;
  }

  start(config: AutosaveConfig): void {
    this.stop();

    this.submissionId = config.submissionId;
    this.getData = config.getData;
    this.canSave = config.canSave ?? (() => true);
    this.beforeSave = config.beforeSave ?? null;
    this.onSaved = config.onSaved ?? null;
    this.baseline = normalizeFormData(config.baseline);
    this.dirty = false;
    this.inFlight = false;
    this.stopped = false;
    this.setState({ status: 'idle', message: null });

    const intervalMs = config.intervalMs ?? FormAutosaveService.DEFAULT_INTERVAL_MS;
    this.timer = setInterval(() => this.tick(), intervalMs);
  }

  stop(): void {
    this.stopped = true;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  // Flags unsaved changes, a spurious call just makes an empty delta
  markDirty(): void {
    if (this.stopped) return;
    this.dirty = true;
    if (this.state.status === 'idle' || this.state.status === 'saved') {
      this.setState({ status: 'pending' });
    }
  }

  // Resets the compare point, e.g. after an explicit save
  rebaseline(data: any): void {
    this.baseline = normalizeFormData(data);
    this.dirty = false;
    if (!this.stopped) {
      this.setState({ status: 'idle', message: null });
    }
  }

  private tick(): void {
    if (!this.dirty || this.inFlight || this.stopped) return;
    if (!this.canSave()) return;
    void this.flush();
  }

  // Sends the pending delta now, safe to call any time
  flush(): Promise<void> {
    if (!this.running) {
      this.running = this.doFlush().finally(() => (this.running = null));
    }
    return this.running;
  }

  // Resolves once a write in progress is done, e.g. before an explicit save
  whenIdle(): Promise<void> {
    return this.running ?? Promise.resolve();
  }

  private async doFlush(): Promise<void> {
    if (this.inFlight || this.stopped) return;
    if (!this.submissionId || !this.getData) return;
    if (!this.canSave()) return;

    // Snapshot at send time, edits during the request go out next cycle
    const snapshot = normalizeFormData(this.getData());
    const delta = computeFormDelta(this.baseline, snapshot);

    if (isEmptyDelta(delta)) {
      this.dirty = false;
      return;
    }

    if (countDeltaPaths(delta) > MAX_DELTA_PATHS) {
      // Almost certainly a bad baseline, re-anchor instead of writing
      console.warn(
        `[autosave] delta of ${countDeltaPaths(delta)} paths looks wrong; re-baselining without saving`
      );
      this.baseline = snapshot;
      this.dirty = false;
      this.setState({ status: 'error', message: 'Autosave skipped an unexpected change set' });
      return;
    }

    this.inFlight = true;
    this.setState({ status: 'saving', message: null });

    try {
      // The gate tells the user why, this just stops
      if (this.beforeSave && !(await this.beforeSave())) {
        this.setState({ status: 'pending', message: null });
        return;
      }

      const response = await this.send(delta, false);

      if (response.status === 401 || response.status === 440) {
        this.stop();
        this.setState({
          status: 'expired',
          message: 'Your session has expired, changes are not being saved',
        });
        return;
      }

      if (!response.ok) {
        this.setState({
          status: 'error',
          message: `Autosave failed (${response.status})`,
        });
        return;
      }

      this.baseline = snapshot;
      this.dirty = false;
      this.setState({ status: 'saved', lastSavedAt: new Date(), message: null });

      // Hand back the stamp so the caller recognises its own write
      if (this.onSaved) {
        let autosavedAt: string | null = null;
        try {
          autosavedAt = (await response.json())?.autosavedAt ?? null;
        } catch {
          // Unreadable body on a 200 is still a success
        }
        this.onSaved(autosavedAt);
      }
    } catch (error: any) {
      // Network hiccup, stay pending and retry next cycle
      this.setState({
        status: 'error',
        message: 'Autosave could not reach the server',
      });
      console.warn('[autosave] request failed:', error?.message || error);
    } finally {
      this.inFlight = false;
    }
  }

  // Last write when the tab closes, fire and forget with keepalive
  flushOnUnload(): void {
    if (this.stopped || !this.dirty || !this.submissionId || !this.getData) return;
    if (!this.canSave()) return;

    const delta = computeFormDelta(this.baseline, normalizeFormData(this.getData()));
    if (isEmptyDelta(delta) || countDeltaPaths(delta) > MAX_DELTA_PATHS) return;

    try {
      void this.send(delta, true);
    } catch {
      // Nothing to do while the page is being torn down
    }
  }

  private send(delta: FormDelta, keepalive: boolean): Promise<Response> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const token = this.authService.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return fetch(`${environment.BACKEND_URL}/submissions/${this.submissionId}/autosave`, {
      method: 'POST',
      keepalive,
      headers,
      body: JSON.stringify({ set: delta.set, unset: delta.unset }),
    });
  }

  private setState(patch: Partial<AutosaveState>): void {
    this.state$.next({ ...this.state$.value, ...patch });
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
